import { fetchClient } from "@/api/api";
import type {
	PlayerAuctionDecideListedInput,
	PlayerAuctionDecideListedInputPath,
	PlayerAuctionDecideListedOutput,
	PlayerAuctionDecideSuggestedInput,
	PlayerAuctionDecideSuggestedInputPath,
	PlayerAuctionDecideSuggestedOutput,
	PlayerAuctionInitiateSellingInput,
	PlayerAuctionInitiateSellingOutput,
	PlayerAuctionPlayersPictureInput,
	PlayerAuctionRecentInput,
	PlayerAuctionRecentOutput,
	PlayersAuctionInitInput,
	PlayersAuctionInitOutput,
	adminPlayersAuctionInput,
	adminPlayersAuctionOutput,
	getPlayersAuctionFacetsInput,
	getPlayersAuctionFacetsOutput,
	playersAuctionInput,
	playersAuctionOutput,
} from "@/api/custom_types";
import useApiError from "@/hooks/useApiError";
import { createFacetSource } from "@/services/facets";
import { userConsoleAtom } from "@/store/console-profile";
import type { ApiError } from "@/utils/fetching";
import {
	type CustomMutationHook,
	type CustomQueryHook,
	invalidateQueries,
} from "@/utils/react-query";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import JSZip, { type JSZipObject } from "jszip";
import { USERS_SERVICE_KEYS } from "../users";
import { PLAYERS_AUCTION_KEYS } from "./keys";
import { hasPendingToBeListed } from "./utils";

const RECENT_PENDING_REFETCH_INTERVAL = 3_000;
const RECENT_IDLE_REFETCH_INTERVAL = 60_000;

export type PicturesByName = Record<string, string>;

export const useGetPlayersAuction: CustomQueryHook<
	playersAuctionOutput,
	playersAuctionInput
> = (payload, options) => {
	return useQuery<playersAuctionOutput, ApiError>({
		...options,
		queryKey: PLAYERS_AUCTION_KEYS.useGetPlayersAuction(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET("/v1/fc/players-auction/", {
				params: { query: payload },
				throwOnError: true,
			});

			if (error) {
				throw error;
			}

			return data;
		},
	});
};

export const useGetAdminPlayersAuction: CustomQueryHook<
	adminPlayersAuctionOutput,
	adminPlayersAuctionInput
> = (payload, options) => {
	return useQuery<adminPlayersAuctionOutput, ApiError>({
		...options,
		queryKey: PLAYERS_AUCTION_KEYS.useGetAdminPlayersAuction(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/admin/players-auction/",
				{
					params: { query: payload },
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
	});
};
export const useGetPlayersAuctionFacets: CustomQueryHook<
	getPlayersAuctionFacetsOutput,
	getPlayersAuctionFacetsInput
> = (payload, options) => {
	return useQuery<getPlayersAuctionFacetsOutput, ApiError>({
		...options,
		queryKey: PLAYERS_AUCTION_KEYS.useGetPlayersAuctionFacets(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/players-auction/facets/",
				{
					params: { query: payload },
					throwOnError: true,
				},
			);

			if (error) {
				throw error;
			}

			return data;
		},
	});
};

export const useGetPlayersAuctionInit: CustomQueryHook<
	PlayersAuctionInitOutput,
	PlayersAuctionInitInput
> = (payload, options) => {
	const setUserConsole = useSetAtom(userConsoleAtom);
	return useQuery<PlayersAuctionInitOutput, ApiError>({
		...options,
		queryKey: PLAYERS_AUCTION_KEYS.useGetPlayersAuctionInit(payload),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/players-auction/init-data/",
				{
					params: { query: payload },
					throwOnError: true,
				},
			);

			if (error) throw error;
			setUserConsole(data.consoles);
			return data;
		},
	});
};

export const usePlayersAuctionInitiateSelling: CustomMutationHook<
	PlayerAuctionInitiateSellingInput,
	PlayerAuctionInitiateSellingOutput
> = () => {
	const { toastApiError } = useApiError();
	return useMutation<
		PlayerAuctionInitiateSellingOutput,
		ApiError,
		PlayerAuctionInitiateSellingInput
	>({
		mutationKey: PLAYERS_AUCTION_KEYS.usePlayersAuctionInitiateSelling(),
		mutationFn: async (body) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/players-auction/initiate-selling",
				{
					body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},

		onSuccess: () => {
			invalidateQueries(PLAYERS_AUCTION_KEYS.useGetPlayerAuctionRecent());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetPlayerAuctionRecent: CustomQueryHook<
	PlayerAuctionRecentOutput,
	PlayerAuctionRecentInput
> = (options) => {
	return useQuery<PlayerAuctionRecentOutput, ApiError>({
		...options,
		queryKey: PLAYERS_AUCTION_KEYS.useGetPlayerAuctionRecent(),
		queryFn: async () => {
			const { data, error } = await fetchClient.GET(
				"/v1/fc/players-auction/recent/",
				{
					throwOnError: true,
				},
			);

			if (error) throw error;
			return data;
		},
		refetchInterval: (query) => {
			const data = query.state.data;

			return hasPendingToBeListed(data)
				? RECENT_PENDING_REFETCH_INTERVAL
				: RECENT_IDLE_REFETCH_INTERVAL;
		},
	});
};

export const usePlayersAuctionDecideSuggestedPlayer: CustomMutationHook<
	{
		path: PlayerAuctionDecideSuggestedInputPath;
		body: PlayerAuctionDecideSuggestedInput;
	},
	PlayerAuctionDecideSuggestedOutput
> = (options) => {
	const { toastApiError } = useApiError();
	return useMutation({
		...options,
		mutationKey: PLAYERS_AUCTION_KEYS.usePlayersAuctionDecideSuggestedPlayer(),
		mutationFn: async ({ path, body }) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/players-auction/suggested/{suggested_player_id}",
				{
					params: { path },
					body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},

		onSuccess: () => {
			invalidateQueries(PLAYERS_AUCTION_KEYS.useGetPlayerAuctionRecent());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const usePlayersAuctionDecideListedPlayer: CustomMutationHook<
	{
		path: PlayerAuctionDecideListedInputPath;
		body: PlayerAuctionDecideListedInput;
	},
	PlayerAuctionDecideListedOutput
> = (options) => {
	const { toastApiError } = useApiError();
	return useMutation({
		...options,
		mutationKey: PLAYERS_AUCTION_KEYS.usePlayersAuctionDecideListedPlayer(),
		mutationFn: async ({ path, body }) => {
			const { data, error } = await fetchClient.POST(
				"/v1/fc/players-auction/listed/{listed_player_id}",
				{
					params: { path },
					body,
					throwOnError: true,
				},
			);
			if (error) throw error;
			return data;
		},

		onSuccess: () => {
			invalidateQueries(PLAYERS_AUCTION_KEYS.useGetPlayerAuctionRecent());
			invalidateQueries(USERS_SERVICE_KEYS.useGetUserWallet());
		},
		onError: (error) => {
			toastApiError(error);
		},
	});
};

export const useGetPlayersPictures: CustomQueryHook<
	PicturesByName,
	PlayerAuctionPlayersPictureInput
> = (payload, options) => {
	return useQuery({
		...options,
		queryKey: PLAYERS_AUCTION_KEYS.useGetPlayersPictures(payload),
		staleTime: 30 * 60 * 1000,
		queryFn: async () => {
			const { data: zipBlob, error } = await fetchClient.GET(
				"/v1/fc/players-auction/players/picture/",
				{
					params: { query: payload },
					parseAs: "blob",
					throwOnError: true,
				},
			);

			if (error) throw error;
			if (!zipBlob) return {};

			const zip = await JSZip.loadAsync(zipBlob);
			const fileEntries = Object.values(zip.files).filter(
				(f): f is JSZipObject => !f.dir,
			);

			const extracted = await Promise.all(
				fileEntries.map(async (f: JSZipObject) => {
					const blob = await f.async("blob");
					const url = URL.createObjectURL(blob);
					const key = f.name.replace(/\.(webp|png|jpg|jpeg)$/i, "");

					return [key, url] as const;
				}),
			);

			return Object.fromEntries(extracted);
		},
	});
};

export const PLAYERS_AUCTION_FACET_SOURCE =
	createFacetSource<getPlayersAuctionFacetsOutput>({
		path: "/v1/fc/players-auction/facets/",
		queryKey: (query) =>
			PLAYERS_AUCTION_KEYS.useGetPlayersAuctionFacets(query as any),
	});
